import { html, TemplateResult } from 'lit';
import {
  clearPendingCatalogSyncAfterUpdate,
  emitShellPendingSyncChanged,
  markPendingEditEntrega,
  markPendingEditReposicion,
  SHELL_PAGE_ACTIVATED_EVENT,
} from '@core/domain/sessionEvents.ts';
import { getErrorMessage } from '@core/domain/utils/errorHandler.ts';
import type { SyncShellPorts } from '@src/core/bootstrap/ShellPorts.ts';
import { warmupShellOfflineAssets } from '@core/bootstrap/factories/offlineAssetWarmup.ts';
import type { PendingSyncRecord } from '@src/modules/kernel/PendingSyncRecord.ts';
import type { SyncStatus } from '@src/modules/system/domain/ports/SyncPorts.ts';
import { LitComponent } from '@src/modules/shell-ui/infrastructure/drivers/ui/components/LitComponent.ts';
import { attachPullToRefresh } from '@src/modules/shell-ui/infrastructure/pullToRefresh.ts';
import {
  formatWhen,
  formatRelativeWhen,
  shellPageHeader,
  shellPageShellClass,
  shellPageErrorBanner,
  shellPageLoadingSkeleton,
  shellPageRefreshBanner,
  shellSectionClass,
} from '@src/modules/shell-ui/infrastructure/shellContentUi.ts';
import type { SyncPushProgress } from '@core/domain/sync/SyncPushResult.ts';
import {
  hideCatalogSyncOverlay,
  hideSyncPushOverlay,
  morphCatalogSyncOverlayToWarmup,
  showCatalogSyncOverlay,
  showSyncPushOverlay,
  updateCatalogSyncPullOverlay,
  updateCatalogSyncWarmupOverlay,
  updateSyncPushOverlay,
} from '@src/modules/shell-ui/infrastructure/shellSyncOverlay.ts';
import { shellModuleRecordListSection } from '@src/modules/shell-ui/infrastructure/shellFormListUi.ts';
import {
  readResilienceLastExportAt,
  readResilienceLastPushSuccessAt,
} from '@src/modules/system/domain/resilienceTimestamps.ts';
import {
  isConnectivityAutoPushEnabled,
  setConnectivityAutoPushEnabled,
} from '@src/modules/system/domain/connectivitySyncPref.ts';

export const SYNC_PULL_FAILED_PREFIX = 'No se pudieron descargar catálogos:';
export const SYNC_PUSH_FAILED_PREFIX = 'No se pudieron enviar registros:';

export function syncIssueCounts(
  entregas: PendingSyncRecord[],
  reposiciones: PendingSyncRecord[],
): { failed: number; pending: number } {
  let failed = 0;
  let pending = 0;
  for (const row of [...entregas, ...reposiciones]) {
    if (row.syncStatus === 'failed') failed += 1;
    else if (row.syncStatus === 'pending') pending += 1;
  }
  return { failed, pending };
}

export function formatPullCatalogMessage(pullError: string | null, mergedCount: number): string {
  if (pullError) return `${SYNC_PULL_FAILED_PREFIX} ${pullError}`;
  if (mergedCount === 0) return 'Catálogos al día: no hubo cambios nuevos.';
  return `Catálogos actualizados: ${mergedCount} ítem(s) nuevo(s).`;
}

export function buildPushFeedbackMessage(opts: {
  pushedEntregas: number;
  pushedReposiciones: number;
  entregaMediaPurgeSkipped?: number;
  reposicionMediaPurgeSkipped?: number;
  toSendBefore: number;
  failed: number;
  pending: number;
  acceptedInferredFromTotales?: boolean;
}): string {
  const parts: string[] = [];
  if (opts.pushedEntregas > 0) parts.push(`${opts.pushedEntregas} entrega(s) enviada(s).`);
  if (opts.pushedReposiciones > 0) parts.push(`${opts.pushedReposiciones} reposición(es) enviada(s).`);
  const mediaSkipped = (opts.entregaMediaPurgeSkipped ?? 0) + (opts.reposicionMediaPurgeSkipped ?? 0);
  if (mediaSkipped > 0) {
    parts.push(`Fotos/firmas locales conservadas (${mediaSkipped}): el servidor no confirmó almacenamiento.`);
  }
  if (opts.acceptedInferredFromTotales) {
    parts.push(
      'El servidor respondió solo con totales; los registros siguen pendientes hasta confirmación explícita. Vuelva a sincronizar.',
    );
  }
  if (opts.toSendBefore === 0) {
    parts.push('No había registros pendientes por enviar.');
  } else if (opts.pushedEntregas + opts.pushedReposiciones === 0 && opts.failed > 0) {
    parts.push(`Ningún registro enviado; ${opts.failed} con error.`);
  } else {
    if (opts.failed > 0) parts.push(`${opts.failed} registro(s) siguen con error.`);
    if (opts.pending > 0) parts.push(`${opts.pending} registro(s) siguen pendientes.`);
    const pushed = opts.pushedEntregas + opts.pushedReposiciones;
    if (pushed > 0 && opts.failed === 0) {
      parts.push('Exporte en Observabilidad para respaldar firmas/fotos y liberar espacio local.');
    }
  }
  return parts.join(' ');
}

export function syncFeedbackIsError(opts: {
  pullError: string | null;
  toSendBefore: number;
  pushedTotal: number;
  failed: number;
}): boolean {
  const pullFailed = Boolean(opts.pullError);
  const pushIncomplete = opts.toSendBefore > 0 && (opts.pushedTotal === 0 || opts.failed > 0);
  return pullFailed || pushIncomplete;
}

export function connectivityAutoPushHint(enabled: boolean): string {
  return enabled
    ? 'Al volver la conexión, la app enviará registros pendientes automáticamente (solo push, nunca catálogos).'
    : 'Al volver la conexión, la app avisará y podrá enviar pendientes con un toque en el aviso.';
}

function syncStatusPanel(p: {
  status: SyncStatus;
  lastPushAt: string | null;
  lastExportAt: string | null;
  autoPushOnReconnect: boolean;
  downloadBusy: boolean;
  uploadBusy: boolean;
  uploadProgress: SyncPushProgress | null;
  pendingCount: number;
  syncMessage: string;
  onDownload: () => void;
  onUpload: () => void;
  onAutoPushChange: (enabled: boolean) => void;
}): TemplateResult {
  const anyBusy = p.downloadBusy || p.uploadBusy;
  return html`
    <section class="${shellSectionClass} md-surface-card">
      <dl class="grid gap-2 text-sm text-[color:var(--md-sys-color-on-surface-variant)] sm:grid-cols-2">
        <div><dt class="font-medium text-[color:var(--md-sys-color-on-surface)]">Última descarga de catálogos</dt><dd>${formatWhen(p.status.lastPullAt)}</dd></div>
        <div><dt class="font-medium text-[color:var(--md-sys-color-on-surface)]">Servidor</dt><dd>${p.status.remoteAvailable ? 'Disponible' : 'No configurado'}</dd></div>
        <div><dt class="font-medium text-[color:var(--md-sys-color-on-surface)]">Último envío exitoso</dt><dd>${formatRelativeWhen(p.lastPushAt)}</dd></div>
        <div><dt class="font-medium text-[color:var(--md-sys-color-on-surface)]">Último respaldo local</dt><dd>${formatRelativeWhen(p.lastExportAt)}</dd></div>
      </dl>
      ${p.status.lastError ? html`<p class="mt-2 text-sm text-[color:var(--md-sys-color-error)]">${p.status.lastError}</p>` : ''}
      ${p.syncMessage
        ? html`<p class="mt-2 text-sm text-[color:var(--md-sys-color-primary)]">${p.syncMessage}</p>`
        : ''}
      ${p.status.remoteAvailable
        ? html`
            <label class="mt-4 flex cursor-pointer items-start gap-3 text-sm text-[color:var(--md-sys-color-on-surface-variant)]">
              <input
                type="checkbox"
                class="mt-1 h-4 w-4 shrink-0 accent-[color:var(--md-sys-color-primary)]"
                .checked=${p.autoPushOnReconnect}
                ?disabled=${anyBusy}
                @change=${(ev: Event) => p.onAutoPushChange((ev.target as HTMLInputElement).checked)}
              />
              <span>
                <span class="font-medium text-[color:var(--md-sys-color-on-surface)]">Enviar automáticamente al reconectar</span>
                <span class="mt-1 block">${connectivityAutoPushHint(p.autoPushOnReconnect)}</span>
              </span>
            </label>
          `
        : ''}
      <div class="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          class="md-btn-filled md-sys-ripple touch-manipulation ${p.downloadBusy ? 'md-btn-filled--busy' : ''}"
          ?disabled=${anyBusy}
          aria-busy=${p.downloadBusy ? 'true' : 'false'}
          @click=${p.onDownload}
        >
          ${p.downloadBusy ? 'Descargando…' : 'Descargar catálogos'}
        </button>
        <button
          type="button"
          class="md-btn-outlined md-sys-ripple touch-manipulation ${p.uploadBusy ? 'md-btn-filled--busy' : ''}"
          ?disabled=${anyBusy || p.pendingCount === 0}
          aria-busy=${p.uploadBusy ? 'true' : 'false'}
          @click=${p.onUpload}
        >
          ${p.uploadBusy ? 'Cargando…' : 'Cargar registros'}
        </button>
      </div>
    </section>
  `;
}

export function syncPageTemplate(p: {
  loading: boolean;
  refreshing: boolean;
  error: string;
  status: SyncStatus | null;
  pendingEntregas: PendingSyncRecord[];
  pendingReposiciones: PendingSyncRecord[];
  downloadBusy: boolean;
  uploadBusy: boolean;
  uploadProgress: SyncPushProgress | null;
  syncMessage: string;
  lastPushAt?: string | null;
  lastExportAt?: string | null;
  autoPushOnReconnect?: boolean;
  onDownload: () => void;
  onUpload: () => void;
  onAutoPushChange?: (enabled: boolean) => void;
  onEditEntrega: (publicId: string) => void;
  onEditReposicion: (publicId: string) => void;
}): TemplateResult {
  if (p.loading) return shellPageLoadingSkeleton(3);
  if (p.error && !p.status) {
    return html`<div class="${shellPageShellClass}">${shellPageErrorBanner(p.error)}</div>`;
  }
  const s = p.status;
  if (!s) return html``;
  const lastPushAt = p.lastPushAt ?? null;
  const lastExportAt = p.lastExportAt ?? null;
  const autoPushOnReconnect = p.autoPushOnReconnect ?? false;
  const onAutoPushChange = p.onAutoPushChange ?? (() => {});

  return html`
    <div class="${shellPageShellClass}">
      ${shellPageHeader(
        'Sincronización',
        'Estado de sincronización',
        'Descarga catálogos actualizados y envía al servidor las entregas y reposiciones pendientes cuando hay conexión.',
      )}
      ${shellPageRefreshBanner(p.refreshing)}
      ${syncStatusPanel({
        status: s,
        lastPushAt,
        lastExportAt,
        downloadBusy: p.downloadBusy,
        uploadBusy: p.uploadBusy,
        uploadProgress: p.uploadProgress,
        pendingCount: p.pendingEntregas.length + p.pendingReposiciones.length,
        syncMessage: p.syncMessage,
        autoPushOnReconnect,
        onDownload: p.onDownload,
        onUpload: p.onUpload,
        onAutoPushChange,
      })}
      ${shellModuleRecordListSection({
        title: 'Entregas pendientes',
        emptyText: 'No hay entregas por enviar al servidor.',
        showCount: true,
        clickable: false,
        rows: p.pendingEntregas.map((row) => ({
          fecha: row.fecha,
          syncStatus: row.syncStatus,
          syncError: row.syncError,
          headline: row.headline,
          subline: row.subline,
          detail: row.detail,
          publicId: row.publicId,
          actions: html`
            <button
              type="button"
              class="md-btn-filled md-sys-ripple touch-manipulation"
              @click=${() => p.onEditEntrega(row.publicId)}
            >
              Editar
            </button>
          `,
        })),
      })}
      ${shellModuleRecordListSection({
        title: 'Reposiciones pendientes',
        emptyText: 'No hay reposiciones por enviar al servidor.',
        showCount: true,
        clickable: false,
        rows: p.pendingReposiciones.map((row) => ({
          fecha: row.fecha,
          syncStatus: row.syncStatus,
          syncError: row.syncError,
          headline: row.headline,
          subline: row.subline,
          medioNombre: row.medioNombre,
          motivoNombre: row.motivoNombre,
          detail: row.detail,
          publicId: row.publicId,
          actions: html`
            <button
              type="button"
              class="md-btn-filled md-sys-ripple touch-manipulation"
              @click=${() => p.onEditReposicion(row.publicId)}
            >
              Editar
            </button>
          `,
        })),
      })}
    </div>
  `;
}

export class SyncPageComponent extends LitComponent {
  shellPorts!: SyncShellPorts;

  private loading = true;
  private errorMessage = '';
  private status: SyncStatus | null = null;
  private pendingEntregas: PendingSyncRecord[] = [];
  private pendingReposiciones: PendingSyncRecord[] = [];
  private downloadBusy = false;
  private uploadBusy = false;
  private uploadProgress: SyncPushProgress | null = null;
  private syncMessage = '';
  private autoPushOnReconnect = isConnectivityAutoPushEnabled();
  private refreshing = false;
  private detachPullRefresh?: () => void;
  private loadGeneration = 0;
  private readonly onPageActivated = (): void => {
    void this.loadPage({ initial: this.loading });
  };

  connectedCallback(): void {
    if (!this.shellPorts) throw new Error('sync-page-component: missing shellPorts');
    super.connectedCallback();
    globalThis.addEventListener(SHELL_PAGE_ACTIVATED_EVENT, this.onPageActivated);
    queueMicrotask(() => {
      this.detachPullRefresh?.();
      this.detachPullRefresh = attachPullToRefresh(
        () => this.closest('.layout-main-scroll'),
        () => this.handleDownload(),
      );
    });
  }

  disconnectedCallback(): void {
    globalThis.removeEventListener(SHELL_PAGE_ACTIVATED_EVENT, this.onPageActivated);
    this.detachPullRefresh?.();
    this.detachPullRefresh = undefined;
    hideCatalogSyncOverlay();
    hideSyncPushOverlay();
    super.disconnectedCallback();
  }

  private async loadPage(opts?: { initial?: boolean }): Promise<void> {
    const generation = ++this.loadGeneration;
    const initial = opts?.initial === true;
    if (initial) this.loading = true;
    else this.refreshing = true;
    this.errorMessage = '';
    this.update();
    try {
      const [status, pending] = await Promise.all([
        this.shellPorts.system.getStatus(),
        this.shellPorts.system.loadSyncPending(),
      ]);
      if (generation !== this.loadGeneration) return;
      this.status = status;
      this.pendingEntregas = pending.entregas;
      this.pendingReposiciones = pending.reposiciones;
      this.autoPushOnReconnect = isConnectivityAutoPushEnabled();
    } catch (e) {
      if (generation !== this.loadGeneration) return;
      this.errorMessage = getErrorMessage(e);
    } finally {
      if (generation === this.loadGeneration) {
        this.loading = false;
        this.refreshing = false;
        this.update();
      }
    }
  }

  private async reloadPending(): Promise<void> {
    const pending = await this.shellPorts.system.loadSyncPending();
    this.pendingEntregas = pending.entregas;
    this.pendingReposiciones = pending.reposiciones;
    emitShellPendingSyncChanged();
  }

  private vibrate(): void {
    try {
      navigator.vibrate?.(12);
    } catch {
      /* WebView / policy may block vibration. */
    }
  }

  private readonly handleDownload = async (): Promise<void> => {
    if (this.downloadBusy || this.uploadBusy) return;
    this.downloadBusy = true;
    this.syncMessage = '';
    this.update();
    showCatalogSyncOverlay();
    try {
      const { mergedCount } = await this.shellPorts.system.pullNow({
        repopulate: true,
        onDownloadProgress: updateCatalogSyncPullOverlay,
      });
      morphCatalogSyncOverlayToWarmup();
      updateCatalogSyncWarmupOverlay({ processed: 0, total: 1 });
      this.syncMessage = 'Preparando mapas y plantillas offline…';
      this.update();
      try {
        await warmupShellOfflineAssets({
          entregaCatalog: this.shellPorts.entregaCatalog,
          onMapProgress: (processed, total) => {
            updateCatalogSyncWarmupOverlay({ processed, total: Math.max(1, total) });
          },
        });
      } catch (warmupErr) {
        const warmupMsg = getErrorMessage(warmupErr);
        this.shellPorts.notifications.warning(`Catálogos descargados; preparación offline incompleta: ${warmupMsg}`);
      } finally {
        hideCatalogSyncOverlay();
      }
      this.status = await this.shellPorts.system.getStatus();
      await this.reloadPending();
      this.syncMessage = formatPullCatalogMessage(null, mergedCount);
      this.shellPorts.notifications.info(this.syncMessage);
      clearPendingCatalogSyncAfterUpdate();
      this.vibrate();
    } catch (e) {
      hideCatalogSyncOverlay();
      this.syncMessage = `${SYNC_PULL_FAILED_PREFIX} ${getErrorMessage(e)}`;
      this.shellPorts.notifications.error(this.syncMessage);
      this.status = await this.shellPorts.system.getStatus().catch(() => this.status);
      await this.reloadPending().catch(() => undefined);
    } finally {
      this.downloadBusy = false;
      this.update();
    }
  };

  private readonly handleEditEntrega = (publicId: string): void => {
    markPendingEditEntrega(publicId);
    this.shellPorts.navigation.navigate('/entrega-paquetes');
  };

  private readonly handleEditReposicion = (publicId: string): void => {
    markPendingEditReposicion(publicId);
    this.shellPorts.navigation.navigate('/reposicion-productos');
  };

  private readonly handleUpload = async (): Promise<void> => {
    if (this.downloadBusy || this.uploadBusy) return;
    const toSendBefore = this.pendingEntregas.length + this.pendingReposiciones.length;
    this.uploadBusy = true;
    this.uploadProgress = { processed: 0, total: Math.max(1, toSendBefore + 1) };
    this.syncMessage = '';
    showSyncPushOverlay(this.uploadProgress);
    this.update();
    try {
      const {
        pushedEntregas,
        pushedReposiciones,
        entregaMediaPurgeSkipped,
        reposicionMediaPurgeSkipped,
        acceptedInferredFromTotales,
      } = await this.shellPorts.system.pushNow({
          onProgress: (progress) => {
            this.uploadProgress = progress;
            updateSyncPushOverlay(progress);
            this.update();
          },
        });
      this.status = await this.shellPorts.system.getStatus();
      await this.reloadPending();
      const { failed, pending } = syncIssueCounts(this.pendingEntregas, this.pendingReposiciones);
      this.syncMessage = buildPushFeedbackMessage({
        pushedEntregas,
        pushedReposiciones,
        entregaMediaPurgeSkipped,
        reposicionMediaPurgeSkipped,
        toSendBefore,
        failed,
        pending,
        acceptedInferredFromTotales,
      });
      const pushedTotal = pushedEntregas + pushedReposiciones;
      if (syncFeedbackIsError({ pullError: null, toSendBefore, pushedTotal, failed })) {
        this.shellPorts.notifications.error(this.syncMessage);
      } else {
        this.shellPorts.notifications.info(this.syncMessage);
      }
      this.vibrate();
    } catch (e) {
      this.syncMessage = `${SYNC_PUSH_FAILED_PREFIX} ${getErrorMessage(e)}`;
      this.shellPorts.notifications.error(this.syncMessage);
      this.status = await this.shellPorts.system.getStatus().catch(() => this.status);
      await this.reloadPending().catch(() => undefined);
    } finally {
      this.uploadBusy = false;
      this.uploadProgress = null;
      hideSyncPushOverlay();
      this.update();
    }
  };

  private readonly handleAutoPushChange = (enabled: boolean): void => {
    setConnectivityAutoPushEnabled(enabled);
    this.autoPushOnReconnect = enabled;
    this.update();
  };

  protected getTemplate(): TemplateResult {
    return syncPageTemplate({
      loading: this.loading,
      refreshing: this.refreshing,
      error: this.errorMessage,
      status: this.status,
      pendingEntregas: this.pendingEntregas,
      pendingReposiciones: this.pendingReposiciones,
      downloadBusy: this.downloadBusy,
      uploadBusy: this.uploadBusy,
      uploadProgress: this.uploadProgress,
      syncMessage: this.syncMessage,
      lastPushAt: readResilienceLastPushSuccessAt(),
      lastExportAt: readResilienceLastExportAt(),
      autoPushOnReconnect: this.autoPushOnReconnect,
      onDownload: () => void this.handleDownload(),
      onUpload: () => void this.handleUpload(),
      onAutoPushChange: this.handleAutoPushChange,
      onEditEntrega: this.handleEditEntrega,
      onEditReposicion: this.handleEditReposicion,
    });
  }
}

customElements.define('sync-page-component', SyncPageComponent);
